import { Card } from '../Card';
import type { BillingUsage } from '../../types';

interface UsageCardProps {
  usage?: BillingUsage;
  isLoading?: boolean;
}

export function UsageCard({ usage, isLoading = false }: UsageCardProps) {
  if (isLoading) {
    return (
      <Card title="Current Usage">
        <div className="text-center py-8 text-muted-foreground">Loading usage data...</div>
      </Card>
    );
  }

  // Defensive checks for missing data
  if (!usage || !usage.api_calls || !usage.identities || !usage.period) {
    return (
      <Card title="Current Usage">
        <div className="text-center py-8 text-muted-foreground">Usage data not available</div>
      </Card>
    );
  }

  const apiCallsPercentage = (usage.api_calls.used / usage.api_calls.limit) * 100;
  const identitiesPercentage = (usage.identities.used / usage.identities.limit) * 100;
  const projectedPercentage = (usage.api_calls.projected_end_of_period / usage.api_calls.limit) * 100;

  const getProgressColor = (percentage: number, hasOverage: boolean = false) => {
    if (hasOverage && percentage > 100) return 'bg-destructive';
    if (percentage >= 100) return 'bg-destructive';
    if (percentage >= 80) return 'bg-warning';
    return 'bg-primary';
  };

  const getTextColor = (percentage: number) => {
    if (percentage >= 100) return 'text-destructive';
    if (percentage >= 80) return 'text-warning';
    return 'text-foreground';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card title="Current Usage">
      <div className="space-y-6">
        {/* Billing Period */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Billing Period</span>
          <span className="font-medium text-foreground">
            {formatDate(usage.period.start)} - {formatDate(usage.period.end)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Days Remaining</span>
          <span className="font-medium text-foreground">{usage.period.days_remaining} days</span>
        </div>

        {/* API Calls Usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">API Calls</span>
            <span className={`text-sm font-semibold ${getTextColor(apiCallsPercentage)}`}>
              {usage.api_calls.used.toLocaleString()} / {usage.api_calls.limit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ${getProgressColor(
                apiCallsPercentage,
                usage.api_calls.overage > 0
              )}`}
              style={{ width: `${Math.min(apiCallsPercentage, 100)}%` }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>{apiCallsPercentage.toFixed(1)}% used</span>
            {usage.api_calls.overage > 0 && (
              <span className="text-destructive font-medium">
                +{usage.api_calls.overage.toLocaleString()} overage
              </span>
            )}
          </div>
        </div>

        {/* Projected Usage Warning */}
        {projectedPercentage > 80 && (
          <div className={`rounded-md p-3 ${projectedPercentage >= 100 ? 'bg-destructive/10 border border-destructive/20' : 'bg-warning/10 border border-warning/20'}`}>
            <div className="flex items-start gap-2">
              <span className={projectedPercentage >= 100 ? 'text-destructive' : 'text-warning'}>
                {projectedPercentage >= 100 ? '⚠️' : 'ℹ️'}
              </span>
              <div className="flex-1">
                <p className={`text-sm font-medium ${projectedPercentage >= 100 ? 'text-destructive' : 'text-warning'}`}>
                  {projectedPercentage >= 100 ? 'Projected Overage' : 'Usage Alert'}
                </p>
                <p className={`text-xs mt-1 ${projectedPercentage >= 100 ? 'text-destructive/80' : 'text-warning/80'}`}>
                  Based on current usage, you're projected to use{' '}
                  <strong>{usage.api_calls.projected_end_of_period.toLocaleString()}</strong> API calls
                  by the end of this period.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Identities Usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Identities</span>
            <span className={`text-sm font-semibold ${getTextColor(identitiesPercentage)}`}>
              {usage.identities.used.toLocaleString()} / {usage.identities.limit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ${getProgressColor(identitiesPercentage)}`}
              style={{ width: `${Math.min(identitiesPercentage, 100)}%` }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>{identitiesPercentage.toFixed(1)}% used</span>
            {identitiesPercentage >= 100 && (
              <span className="text-destructive font-medium">Limit reached</span>
            )}
          </div>
        </div>

        {/* Overage Information (for paid tiers) */}
        {usage.api_calls.overage > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="bg-info/10 border border-info/20 rounded-md p-3">
              <h4 className="text-sm font-medium text-info mb-1">Overage Billing</h4>
              <p className="text-xs text-info">
                Overage charges are calculated daily and reported to Paddle. You will be billed for overages
                at the end of your billing period.
              </p>
            </div>
          </div>
        )}

        {/* Hard Limit Warning (developer tier) */}
        {(apiCallsPercentage >= 90 || identitiesPercentage >= 90) && usage.api_calls.limit <= 20000 && (
          <div className="pt-4 border-t border-border">
            <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
              <h4 className="text-sm font-medium text-warning mb-1">Approaching Limit</h4>
              <p className="text-xs text-warning/80">
                Free tier plans have hard limits. Upgrade to a paid plan for higher limits and soft quotas.
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
